import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import config from '../../config/environment';

// Snow animation keyframes
const snowfall = keyframes`
  0% {
    transform: translateY(-10px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
    opacity: 0;
  }
`;

const Snowflake = styled.div`
  position: absolute;
  top: -10px;
  left: ${props => props.left}%;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: white;
  border-radius: 50%;
  opacity: 0.8;
  animation: ${snowfall} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
`;

const NewsletterContainer = styled.div`
  background-color: ${props => props.theme.colors.primary};
  padding: 2.5rem 4rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: auto;
  box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.3);
  font-family: ${props => props.theme.fonts.main};
  
  @media (max-width: 1024px) {
    padding: 2rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }
`;

const TextContainer = styled.div`
  h2 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 3rem;
    margin: 0;
    letter-spacing: 0.5px;
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }
  
  p {
    margin: 0.5rem 0 0 0;
    font-size: 1.3rem;
    opacity: 0.9;
    letter-spacing: 0.3px;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const FormContainer = styled.form`
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-left: 1rem;
  
  @media (max-width: 1024px) {
    gap: 1rem;
    margin-left: 0.5rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const Input = styled.input`
  padding: 0.9rem 1.2rem;
  width: 170px;
  border: none;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.08);
  color: white;
  font-size: 1rem;
  transition: all 0.2s ease;
  font-family: ${props => props.theme.fonts.main};
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
    font-family: ${props => props.theme.fonts.main};
  }
  
  &:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 0.9rem 2.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 1px;
  transition: all 0.2s ease;
  font-family: ${props => props.theme.fonts.main};
  
  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const StatusMessage = styled.div`
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.9rem;
  color: ${props => props.error ? '#E53E3E' : '#48BB78'};
  transition: all 0.3s ease;
`;

const WinterNewsletterContainer = styled.div`
  background: #101d40;
  padding: 3rem 4rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: auto;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  font-family: ${props => props.theme.fonts.main};
  
  @media (max-width: 1024px) {
    padding: 2.5rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
    padding: 2rem;
  }
`;

const WinterTextContainer = styled.div`
  position: relative;
  z-index: 1;
  
  h2 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 3.5rem;
    margin: 0;
    letter-spacing: 0.5px;
    font-weight: 600;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    margin: 1rem 0 0 0;
    font-size: 1.4rem;
    opacity: 0.9;
    letter-spacing: 0.3px;
    color: white;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
`;

const WinterFormContainer = styled.form`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-left: 2rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 1024px) {
    gap: 0.75rem;
    margin-left: 1rem;
  }
   
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const WinterInput = styled.input`
  padding: 1rem 1.2rem;
  width: 180px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  backdrop-filter: blur(5px);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
    font-family: ${props => props.theme.fonts.main};
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const WinterSubmitButton = styled.button`
  background: linear-gradient(135deg, #E53E3E 0%, #C53030 100%);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
    background: linear-gradient(135deg, #C53030 0%, #9B2C2C 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const parseResponse = async (response) => {
  if (!response) {
    return { data: null, message: null };
  }

  try {
    const json = await response.clone().json();
    return {
      data: json,
      message: typeof json?.message === 'string' ? json.message : null
    };
  } catch {
    try {
      const text = await response.clone().text();
      return {
        data: null,
        message: text || null
      };
    } catch {
      return { data: null, message: null };
    }
  }
};

const NewsletterSignUp = ({ tag }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    status: null
  });

  // Generate snowflakes for winter theme
  const [snowflakes, setSnowflakes] = useState([]);
  
  useEffect(() => {
    if (tag === 'Christmas 2025') {
      const flakes = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 5 + 5,
        delay: Math.random() * 5
      }));
      setSnowflakes(flakes);
    }
  }, [tag]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, status: null });

    try {
      const response = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': config.frontendUrl
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({
          ...formData,
          origin: window.location.origin,
          tags: tag ? [tag] : ['Newsletter']
        })
      });
      const { data, message } = await parseResponse(response);

      if (!response.ok || data?.success === false) {
        throw new Error(
          (data && typeof data.message === 'string' && data.message) ||
          message ||
          `Subscription failed${response.status ? ` (${response.status})` : ''}`
        );
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        status: data?.status ?? null
      });
      
      if (data?.status === 'new_subscription') {
        setFormData({ firstName: '', lastName: '', email: '' });
      }
    } catch (error) {
      setStatus({
        submitting: false,
        error: error.message || 'An error occurred while subscribing. Please try again.',
        success: false,
        status: null
      });
    }
  };

  // Use winter theme for Christmas 2025
  if (tag === 'Christmas 2025') {
    return (
      <WinterNewsletterContainer>
        {snowflakes.map(flake => (
          <Snowflake
            key={flake.id}
            left={flake.left}
            size={flake.size}
            duration={flake.duration}
            delay={flake.delay}
          />
        ))}
        <WinterTextContainer>
          <h2>Keep up to date</h2>
          <p>Sign up to be the first to hear about what's coming this winter</p>
        </WinterTextContainer>
        
        <WinterFormContainer onSubmit={handleSubmit}>
          <WinterInput 
            type="text" 
            name="firstName"
            placeholder="First Name" 
            value={formData.firstName}
            onChange={handleChange}
          />
          <WinterInput 
            type="text" 
            name="lastName"
            placeholder="Last Name" 
            value={formData.lastName}
            onChange={handleChange}
          />
          <WinterInput 
            type="email" 
            name="email"
            placeholder="Email" 
            value={formData.email}
            onChange={handleChange}
          />
          <WinterSubmitButton 
            type="submit" 
            disabled={status.submitting}
          >
            {status.submitting ? 'SIGNING UP...' : 'SIGN UP'}
          </WinterSubmitButton>
          
          {status.error && (
            <StatusMessage error>{status.error}</StatusMessage>
          )}
          {status.success && (
            <StatusMessage>
              {status.status === 'already_subscribed' 
                ? 'You are already subscribed to our newsletter!'
                : status.status === 'pending_confirmation'
                  ? 'Thank you for subscribing! Please check your email to confirm your subscription.'
                  : status.status === 'new_subscription'
                    ? 'Thank you for subscribing! You have been successfully added to our newsletter.'
                    : 'Thank you for subscribing!'}
            </StatusMessage>
          )}
        </WinterFormContainer>
      </WinterNewsletterContainer>
    );
  }

  // Default theme for other pages
  return (
    <NewsletterContainer>
      <TextContainer>
        <h2>Keep up to date</h2>
        <p>Sign up to be the first to hear about upcoming events</p>
      </TextContainer>
      
      <FormContainer onSubmit={handleSubmit}>
        <Input 
          type="text" 
          name="firstName"
          placeholder="First Name" 
          value={formData.firstName}
          onChange={handleChange}
        />
        <Input 
          type="text" 
          name="lastName"
          placeholder="Last Name" 
          value={formData.lastName}
          onChange={handleChange}
        />
        <Input 
          type="email" 
          name="email"
          placeholder="Email" 
          value={formData.email}
          onChange={handleChange}
        />
        <SubmitButton 
          type="submit" 
          disabled={status.submitting}
        >
          {status.submitting ? 'SIGNING UP...' : 'SIGN UP'}
        </SubmitButton>
        
        {status.error && (
          <div style={{ color: 'red', marginTop: '10px' }}>{status.error}</div>
        )}
        {status.success && (
          <div style={{ color: 'green', marginTop: '10px' }}>
            {status.status === 'already_subscribed' 
              ? 'You are already subscribed to our newsletter!'
              : status.status === 'pending_confirmation'
                ? 'Thank you for subscribing! Please check your email to confirm your subscription.'
                : status.status === 'new_subscription'
                  ? 'Thank you for subscribing! You have been successfully added to our newsletter.'
                  : 'Thank you for subscribing!'}
          </div>
        )}
      </FormContainer>
    </NewsletterContainer>
  );
};

export default NewsletterSignUp;
